import React from 'react';
import qs from 'qs';
import { Box, IconButton } from '@material-ui/core';
import { DataGrid, GridCellParams, GridColumns } from '@material-ui/data-grid';
import { FiberManualRecord, OpenInNew } from '@material-ui/icons';
import { DateTime } from 'luxon';
import { useCampfireFetch } from '../../../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../../../global/config/useApiUrl';
import { VolunteerProfile } from '../../../../../../../../common/AvatarName';
import { useApplyCustomWords } from '../../../useCustomWords';
import { CommonRequiringActionWidgetProps } from '../../shared';

export const CheckedInVolunteersTableWidget = (props: CommonRequiringActionWidgetProps) => {
  const cw = useApplyCustomWords();
  const { filters } = props;
  const apiUrl = useApiUrl();

  const { programIds, activityIds } = JSON.parse(filters) as {
    programIds?: string[];
    activityIds?: string[];
  };

  const campfireQuery = useCampfireFetch({ defer: true });
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  const fetchData = React.useCallback(() => {
    setLoading(true);
    campfireQuery
      .run({
        url: `${apiUrl}/vm/dashboard/management/volunteers/checked-in/table`,
        params: {
          filters: {
            programIds: programIds || [],
            activityIds: activityIds || [],
          },
          page: 1,
          limit: 200,
        },
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'brackets', encode: false });
        },
      })
      .then((response) => {
        if (response?.data?.data) {
          const mapped = response.data.data.data.map((item: any) => ({
            id: item.cico_id,
            volunteerId: item.volunteer_id,
            profileId: item.profile_id,
            userId: item.user_id,
            firstName: item.first_name,
            lastName: item.last_name,
            preferredName: item.preferred_name,
            avatarUrl: item.avatar_object_url,
            activityId: item.activity_id,
            activityName: item.activity_name,
            programName: item.program_name,
            sessionName: item.session_name,
            checkIn: item.check_in,
            activityDate: item.activity_date,
          }));
          setRows(mapped);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filters]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatCheckInTime = (checkIn: string) => {
    const dt = DateTime.fromSQL(checkIn);
    if (!dt.isValid) return checkIn;
    return dt.toFormat('h:mm a').toLowerCase();
  };

  const columns: GridColumns = [
    {
      field: 'volunteerName',
      headerName: 'Volunteer',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 180,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <VolunteerProfile
            inTable
            name={`${row.preferredName} ${row.lastName}`}
            lastName={row.firstName}
            profileId={row.profileId}
            preferredName={row.preferredName}
            userId={row.userId}
          />
        );
      },
    },
    {
      field: 'activityName',
      headerName: cw('Activity'),
      flex: 1,
      disableColumnMenu: true,
      minWidth: 150,
    },
    {
      field: 'sessionName',
      headerName: 'Session',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 120,
    },
    {
      field: 'checkIn',
      headerName: 'Check-In',
      flex: 0.7,
      disableColumnMenu: true,
      minWidth: 110,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <Box display='flex' alignItems='center'>
            <FiberManualRecord style={{ fontSize: '10px', color: '#4caf50', marginRight: '6px' }} />
            {formatCheckInTime(row.checkIn)}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <IconButton
            size='small'
            onClick={() => {
              window.open(
                `/volunteering/activity-report?tab=incomplete&activityId=${row.activityId}&activityDate=${row.activityDate}`,
                '_blank'
              );
            }}
          >
            <OpenInNew style={{ fontSize: '16px' }} />
          </IconButton>
        );
      },
    },
  ];

  return (
    <Box width='100%' height='100%'>
      <DataGrid columns={columns} rows={rows} loading={loading} autoPageSize disableSelectionOnClick />
    </Box>
  );
};
